import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  afterNextRender,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import type { PublicGalleryPhoto } from './public-photo.models';
import { MasonryGridComponent } from '@lite-workspace/shared/blocks/masonry-grid';
import { GalleryPhotoCardComponent } from './gallery-photo-card.component';

// The public-client projection of the shared masonry grid: this wrapper owns the
// gallery photo CARD (and the preview derivative it needs), the shared grid owns
// the column geometry. Column COUNT comes from the design «Размер фото» × the
// viewport tier and the GAP from «Сетка отступов» — the view page owns that
// decision and passes both in.
// ⚠️ The view page derives the lightbox flat order from the SAME pure packer the
// grid uses, keeping on-screen order ↔ viewer order in lockstep.
@Component({
  selector: 'gallery-masonry',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [GalleryPhotoCardComponent, MasonryGridComponent],
  template: `
    <fcr-masonry-grid [items]="photos()" [columnCount]="columnCount()" [gapPx]="gapPx()">
      <ng-template let-photo>
        <gallery-photo-card
          [photo]="photo"
          [cellWidth]="cellWidth()"
          [densityScale]="densityScale()"
          [clientRole]="clientRole()"
          [heartable]="heartable()"
          [hearted]="heartedIds().has(photo.id)"
          [heartPending]="heartPending()"
          [downloadable]="downloadable()"
          [downloadDisabledForScene]="downloadDisabledForScene()"
          [noteable]="noteable()"
          [hasNote]="commentedIds().has(photo.id)"
          [privatable]="privatable()"
          (open)="photoOpen.emit(photo.id)"
          (heart)="photoHeart.emit(photo.id)"
          (download)="photoDownload.emit(photo.id)"
          (note)="photoNote.emit(photo.id)"
          (togglePrivate)="photoTogglePrivate.emit(photo.id)"
          (imageError)="photoImageError.emit()"
        />
      </ng-template>
    </fcr-masonry-grid>
  `,
})
export class GalleryMasonryComponent {
  readonly photos = input.required<PublicGalleryPhoto[]>();
  readonly columnCount = input.required<number>();
  readonly gapPx = input.required<number>();
  // A shrunk-by-transform host (the design preview) renders the tree at a VIRTUAL width: the
  // derivative must follow density × that scale, not the virtual cell width (spec «Дизайн
  // галереи» item 50). The public page renders 1:1 and keeps the default.
  readonly densityScale = input(1);
  readonly clientRole = input(false);
  readonly heartable = input(false);
  readonly heartedIds = input<ReadonlySet<string>>(new Set<string>());
  readonly heartPending = input(false);
  readonly downloadable = input(false);
  // The scene-level download exclusion — the whole masonry belongs to one scene.
  readonly downloadDisabledForScene = input(false);
  readonly noteable = input(false);
  readonly commentedIds = input<ReadonlySet<string>>(new Set<string>());
  readonly privatable = input(false);
  readonly photoOpen = output<string>();
  readonly photoHeart = output<string>();
  readonly photoDownload = output<string>();
  readonly photoNote = output<string>();
  readonly photoTogglePrivate = output<string>();
  readonly photoImageError = output<void>();

  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly containerWidth = signal(0);

  constructor() {
    afterNextRender(() => {
      const observer = new ResizeObserver(entries => {
        const width = entries[0]?.contentRect.width ?? 0;
        if (width > 0) this.containerWidth.set(width);
      });
      observer.observe(this.host.nativeElement);
      this.destroyRef.onDestroy(() => observer.disconnect());
    });
  }

  // One cell width for all columns — each tile picks its own rendition rung from it, since the
  // rung a portrait needs differs from the one a landscape of the same cell needs.
  protected readonly cellWidth = computed(() => {
    const width = this.containerWidth();
    const count = Math.max(1, this.columnCount());
    return width > 0 ? (width - this.gapPx() * (count - 1)) / count : 360;
  });
}
